import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../context/I18nContext";
import { formatApiErrorDetail } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Sprout, Loader2 } from "lucide-react";
import { toast } from "sonner";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function Login() {
  const { login } = useAuth();
  const { t } = useI18n();
  const nav = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setErr("");
    try {
      await login(form.email, form.password);
      toast.success("Berhasil masuk");
      nav("/dashboard");
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  const google = () => {
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between p-12 text-white" style={{ background: "linear-gradient(135deg,#022C22 0%,#064E3B 60%,#047857 100%)" }}>
        <div className="flex items-center gap-2">
          <div className="w-10 h-10 rounded-lg bg-emerald-500/20 border border-emerald-400/30 flex items-center justify-center">
            <Sprout className="w-5 h-5 text-emerald-200" />
          </div>
          <div className="font-bold text-xl" style={{ fontFamily: "Outfit" }}>SatuCRM</div>
        </div>
        <div>
          <h1 className="text-4xl font-bold leading-tight mb-4" style={{ fontFamily: "Outfit" }}>
            Simple CRM for <br /> Bigger Opportunities.
          </h1>
          <p className="text-emerald-100/80 max-w-md">
            Kelola leads, pipeline penjualan, aktivitas, follow-up, dan pelanggan dalam satu platform yang dibuat untuk UMKM Indonesia.
          </p>
          <div className="grid grid-cols-3 gap-4 mt-10 max-w-md">
            {[["120+","Leads"],["Rp482M","Pipeline"],["15%","Konversi"]].map(([n,l])=>(
              <div key={l} className="rounded-xl bg-white/5 border border-white/10 p-4">
                <div className="text-2xl font-bold">{n}</div>
                <div className="text-xs text-emerald-200/70">{l}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="text-xs text-emerald-200/60">© 2026 SatuCRM · From Leads to Loyal Customers</div>
      </div>

      <div className="flex items-center justify-center p-6 lg:p-12 bg-slate-50">
        <div className="w-full max-w-md">
          <h2 className="text-3xl font-bold text-slate-900" style={{ fontFamily: "Outfit" }}>{t("auth.welcome")}</h2>
          <p className="text-sm text-slate-500 mt-2">{t("auth.welcomeSub")}</p>

          <button onClick={google} data-testid="google-login-btn"
            className="mt-6 w-full flex items-center justify-center gap-3 border border-slate-300 bg-white rounded-lg py-2.5 text-sm font-medium hover:bg-slate-50 transition">
            <svg width="18" height="18" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.6-6 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.5 6.1 29.5 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20c11 0 20-8.9 20-20 0-1.3-.1-2.3-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.6 15.8 18.9 13 24 13c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.5 6.1 29.5 4 24 4 16.3 4 9.7 8.4 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.3 0 10.1-2 13.8-5.3l-6.4-5.4c-2 1.4-4.6 2.2-7.4 2.2-5.3 0-9.8-3.4-11.3-8L6 32.3C9.4 39 15.9 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.7 2-2.1 3.9-4 5.3l6.4 5.4C41 34.4 44 29.5 44 24c0-1.3-.1-2.3-.4-3.5z"/></svg>
            {t("auth.google")}
          </button>

          <div className="my-6 flex items-center gap-3 text-xs text-slate-400"><div className="flex-1 h-px bg-slate-200"/> {t("auth.or")} <div className="flex-1 h-px bg-slate-200"/></div>

          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email">{t("auth.email")}</Label>
              <Input id="email" type="email" required data-testid="login-email"
                value={form.email} onChange={(e)=>setForm({...form,email:e.target.value})}
                placeholder="you@company.com" />
            </div>
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">{t("auth.password")}</Label>
                <Link to="/forgot-password" className="text-xs text-emerald-800 hover:underline">{t("auth.forgot")}</Link>
              </div>
              <Input id="password" type="password" required data-testid="login-password"
                value={form.password} onChange={(e)=>setForm({...form,password:e.target.value})}
                placeholder="••••••••" />
            </div>
            {err && <div className="text-sm text-rose-600" data-testid="login-error">{err}</div>}
            <Button type="submit" disabled={loading} className="brand-btn w-full h-11" data-testid="login-submit">
              {loading ? <Loader2 className="w-4 h-4 animate-spin"/> : t("auth.login")}
            </Button>
          </form>

          <p className="text-sm text-slate-500 mt-6 text-center">
            {t("auth.noAccount")} <Link to="/register" className="text-emerald-800 font-medium hover:underline" data-testid="go-register">{t("auth.register")}</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
